import { Icon, Text, VStack } from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { memo } from 'react'

import type { SwitchboardTranslateFn } from '../../types'
import { text } from '../../utils/text'

export interface SwitchboardEmptyStateProps {
  /** Type of empty state to display */
  type: 'no-data' | 'no-results' | 'no-search-results'
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
}

/**
 * Empty state component for switchboard list
 * Displays appropriate message based on the type of empty state
 */
export const SwitchboardEmptyState = memo<SwitchboardEmptyStateProps>(
  ({ type, t }) => {
    if (type === 'no-data') {
      return (
        <VStack
          w="100%"
          h="100%"
          justifyContent="center"
          alignItems="center"
          gap={4}
        >
          <Icon boxSize={16} color="blue.solid">
            <svg
              viewBox="0 0 37 37"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15.4168 12.3327H15.4322M18.5002 18.4993H18.5156M21.5835 12.3327H21.5989M24.6668 18.4993H24.6822M27.7502 12.3327H27.7656M9.25016 12.3327H9.26558M10.7918 24.666H26.2085M12.3335 18.4993H12.3489M6.16683 6.16602H30.8335C32.5364 6.16602 33.9168 7.54647 33.9168 9.24935V27.7493C33.9168 29.4522 32.5364 30.8327 30.8335 30.8327H6.16683C4.46395 30.8327 3.0835 29.4522 3.0835 27.7493V9.24935C3.0835 7.54647 4.46395 6.16602 6.16683 6.16602Z" />
            </svg>
          </Icon>
          <VStack gap={1}>
            <Text fontWeight="semibold" fontSize="lg">
              {text(
                t,
                'switchboard.list.empty.noDataTitle',
                'No switchboards yet',
              )}
            </Text>
            <Text color="fg.muted" fontSize="sm">
              {text(
                t,
                'switchboard.list.empty.noDataDescription',
                'Get started by creating your first switchboard',
              )}
            </Text>
          </VStack>
        </VStack>
      )
    }

    if (type === 'no-results') {
      return (
        <VStack
          w="100%"
          h="100%"
          justifyContent="center"
          alignItems="center"
          gap={4}
        >
          <Icon boxSize={16} color="fg.muted">
            <Search size="100%" />
          </Icon>
          <VStack gap={1}>
            <Text fontWeight="semibold" fontSize="lg">
              {text(
                t,
                'switchboard.list.empty.noResultsTitle',
                'No switchboards on this page',
              )}
            </Text>
            <Text color="fg.muted" fontSize="sm">
              {text(
                t,
                'switchboard.list.empty.noResultsDescription',
                'Try adjusting your page size or navigate to a different page',
              )}
            </Text>
          </VStack>
        </VStack>
      )
    }

    return (
      <VStack
        w="100%"
        h="100%"
        justifyContent="center"
        alignItems="center"
        gap={4}
      >
        <Icon boxSize={16} color="fg.muted">
          <Search size="100%" />
        </Icon>
        <VStack gap={1}>
          <Text fontWeight="semibold" fontSize="lg">
            {text(
              t,
              'switchboard.list.empty.noSearchResultsTitle',
              'No results found',
            )}
          </Text>
          <Text color="fg.muted" fontSize="sm">
            {text(
              t,
              'switchboard.list.empty.noSearchResultsDescription',
              'Try adjusting your search terms',
            )}
          </Text>
        </VStack>
      </VStack>
    )
  },
)

SwitchboardEmptyState.displayName = 'SwitchboardEmptyState'
